import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../UserContext";
import { MenuWidthContext } from "../MenuContext";
import axios from "axios";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Avatar,
  Tooltip,
  MenuItem,
  Badge,
  Divider,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  LocalHospital,
  NotificationsActive,
  Person,
  ExitToApp,
  VpnKey,
  Dashboard,
  Settings,
  Speed,
} from '@mui/icons-material';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(-100%); }
  to { transform: translateY(0); }
`;

// Styled Components
const CleanAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  color: '#333',
  boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
  position: 'fixed',
  top: 0,
  width: `calc(100% - ${props => props.menuwidth}px)`,
  marginLeft: `${props => props.menuwidth}px`,
  animation: `${slideIn} 0.5s ease-out`,
  borderBottom: '1px solid rgba(231, 235, 240, 0.8)',
}));

const LogoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  '&:hover svg': {
    animation: `${pulse} 1s ease-in-out`,
  }
}));

const EmergencyBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#dc3545',
    color: 'white',
    animation: `${pulse} 2s infinite`,
  }
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: '#495057',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(67, 97, 238, 0.08)',
    transform: 'translateY(-1px)',
  }
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4361ee 0%, #3f37c9 100%)',
  width: 38,
  height: 38,
  fontSize: '1rem',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(67, 97, 238, 0.2)',
  border: '2px solid #ffffff',
}));

const MenuButton = styled(MenuItem)(({ theme }) => ({
  padding: '12px 24px',
  gap: '12px',
  borderRadius: '8px',
  margin: '0 8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(67, 97, 238, 0.08)',
    transform: 'translateX(4px)',
  }
}));

const StatusIndicator = styled('div')(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#22c55e',
  marginLeft: 8,
}));



const ProfileAvatar = ({ userProfile, userName }) => {
  return (
    <UserAvatar
      src={userProfile} 
      alt={userName}
      onError={(e) => e.target.src = ''} 
    >
      {!userProfile && userName?.charAt(0).toUpperCase()}  
    </UserAvatar>
  );
};

const Header = () => {
  const history = useNavigate();
  const userdata = useContext(UserContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationCount, setNotificationCount] = useState(3);
  const { menuWidth } = useContext(MenuWidthContext);
  const [widths, setWidths] = useState(menuWidth);

  // const user = JSON.parse(userdata);

  const [user, setUser] = useState(() => {
    const localData = localStorage.getItem("userData");
    return localData ? JSON.parse(localData) : {};
  });
  
  const userName = user?.name || "";

  useEffect(() => {
    setWidths(menuWidth);

    const handleUserDataUpdated = (event) => {
      const newUserData = event.detail;
      setUser(newUserData);
    };

    window.addEventListener('userDataUpdated', handleUserDataUpdated);
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdated);
    };

  }, [menuWidth]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const logout = async () => {
    try {
      await axios.delete("/logout");
      localStorage.clear();
      history("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const changePassword = () => {
    history("/changePassword");
    handleClose();
  };

  const profile = () => {
    history("/profile");
    handleClose();
  };

  return (
    <CleanAppBar 
    elevation={0} 
      position="fixed"
      sx={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        color: '#333',
        boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
        top: 0,
        width: `calc(100% - ${widths}px)`, // ✅ Dynamic width
        marginLeft: `${widths}px`, // ✅ Aligns properly with menu
        animation: `${slideIn} 0.5s ease-out`,
        borderBottom: '1px solid rgba(231, 235, 240, 0.8)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
        <LogoSection>
          <LocalHospital sx={{ 
            color: '#4361ee', 
            fontSize: 32,
            filter: 'drop-shadow(0 2px 4px rgba(67, 97, 238, 0.2))'
          }} />
          <Box>
            <Typography
              variant="h5"
              component="div"
              sx={{
                color: '#1e293b',
                fontWeight: 700,
                fontSize: '1.3rem',
                letterSpacing: '-0.5px',
              }}
            >
              ለAmbulance
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontWeight: 500,
              }}
            >
              Emergency Response Unit
            </Typography>
          </Box>
        </LogoSection>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* <Tooltip title="System Status">
            <StyledIconButton>
              <Speed />
            </StyledIconButton>
          </Tooltip>

          <Tooltip title="Dashboard">
            <StyledIconButton>
              <Dashboard />
            </StyledIconButton>
          </Tooltip> */}

          {/* <Tooltip title="Active Emergencies">
            <StyledIconButton>
              <EmergencyBadge badgeContent={notificationCount}>
                <NotificationsActive />
              </EmergencyBadge>
            </StyledIconButton>
          </Tooltip> */}

          {/* <Tooltip title="Settings">
            <StyledIconButton>
              <Settings />
            </StyledIconButton>
          </Tooltip> */}

          <Box sx={{ position: 'relative' }}>
            <Tooltip title="Account settings">
              <StyledIconButton
                onClick={handleMenu}
                size="small"
                sx={{ 
                  border: '1px solid rgba(231, 235, 240, 0.8)',
                  borderRadius: '12px',
                  padding: '8px',
                  '&:hover': {
                    borderColor: '#4361ee',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <ProfileAvatar userProfile={user.profilePictureURL} userName={user.name} />
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    > 
                      {userName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                        }}
                      >
                        Online
                      </Typography>
                      <StatusIndicator />
                    </Box>
                  </Box>
                </Box>
              </StyledIconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                  borderRadius: '12px',
                  minWidth: '240px',
                  border: '1px solid rgba(231, 235, 240, 0.8)',
                },
              }}
              sx={{
                '& .MuiMenu-paper': {
                  right: '0 !important',
                  left: 'auto !important',
                }
              }}
            >
              <Box sx={{ px: 3, py: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Welcome back,
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {userName}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ py: 1 }}>
                <MenuButton onClick={profile}>
                  <Person fontSize="small" />
                  Profile
                </MenuButton>
                <MenuButton onClick={changePassword}>
                  <VpnKey fontSize="small" />
                  Change Password
                </MenuButton>
              </Box>
              <Divider />
              <Box sx={{ py: 1 }}>
                <MenuButton 
                  onClick={logout} 
                  sx={{ 
                    color: '#dc3545',
                    '&:hover': {
                      backgroundColor: 'rgba(220, 53, 69, 0.08)',
                    }
                  }}
                >
                  <ExitToApp fontSize="small" />
                  Logout
                </MenuButton>
              </Box>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </CleanAppBar>
  );
};

export default Header;